'use client';

import { useId } from 'react';
import { lookupIcon } from 'heroicons-lookup';
import { XMarkIcon } from '@heroicons/react/24/solid';

import InfoTooltip from '@components/info-tooltip';

const TextInput = ({ ref, ...props }) => {
    const { 
        id, 
        type, 
        variant, 
        label, 
        help, 
        icon,
        showHelp, 
        helpColor, 
        className, 
        required, 
        tooltip,
        ariaLabel, 
        autocomplete,
        errorIcon,
        onChange, 
        onIconClick, 
        ...otherProps 
    } = props;
    const newId = useId();
    const inputId = id ? id : newId;
    const ariaLabelId = `${inputId}-label`;

    const inputType = type ? type : "text";
    let inputClass = "w-full p-3 font-vg-book text-base bg-grey04 border rounded-md outline-none placeholder-grey";
    const useVariant = variant ? variant : "default"
    switch (useVariant) {
        case "default":
            inputClass = `${inputClass} text-black border-grey hover:border-blue focus:border-blue focus:border-2`;
            break;
        case "error":
            inputClass = `${inputClass} text-red border-red border-2`;
            break;
        case "success":
            inputClass = `${inputClass} text-green04 border-green04 hover:border-blue focus:border-blue focus:border-2`;
            break;
        case "filled":
            inputClass = `${inputClass} text-black border-grey hover:border-blue focus:border-blue focus:border-2`;
            break;
        case "disabled":
            inputClass = `${inputClass} text-grey border-grey03 hover:border-grey03 focus:border-grey03`;
            break;
        case "active":
            inputClass = `${inputClass} text-grey border-blue`;
            break;
        case "indicator-title":
            inputClass = `${inputClass} text-blue border-grey hover:border-blue focus:border-blue focus:border-2`;
            inputClass = inputClass.replace("font-vg-book", "font-vg-medium");
            break;
        default:
            inputClass = `${inputClass} text-black border-grey hover:border-blue focus:border-blue focus:border-2`;
            break;
    }
    let Icon = null;
    if (icon) {
        Icon = lookupIcon(icon, "outline");
    }

    const getLabel = () => {
        if (tooltip) {
            if (required) {
                return (
                    <div className="flex items-center gap-2 mb-1.5">
                        <label htmlFor={inputId} className="block font-vg-book text-base leading-120">
                            <span className="text-grey">{label}</span><span className="text-red">&nbsp;&nbsp;*</span>
                        </label>
                        <InfoTooltip content={tooltip} position="top"/>
                    </div>
                );
            } else {
                return (
                    <div className="flex items-center gap-2 mb-1.5">
                        <label htmlFor={inputId} className="block font-vg-book text-base leading-120 text-grey">
                            {label}
                        </label>
                        <InfoTooltip content={tooltip} position="top"/>
                    </div>
                );
            }
        } else {
            if (required) {
                return (
                    <label htmlFor={inputId} className="block font-vg-book text-base leading-120 mb-1.5">
                        <span className="text-grey">{label}</span><span className="text-grey">&nbsp;&nbsp;*</span>
                    </label>
                );
            } else {
                return (
                    <label htmlFor={inputId} className="block font-vg-book text-base leading-120 text-grey mb-1.5">
                        {label}
                    </label>
                );
            }
        }
    };
    
    return (
        <div className={className}>
            {label && getLabel()}
            <div className="relative">
                {Icon && ( onIconClick ? (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer" onClick={onIconClick}>
                        <Icon className="color-black h-6"/>
                    </div>
                ) : (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <Icon className="color-black h-6"/>
                    </div>
                ))}
                {ariaLabel ? (
                    <>
                        <input
                            ref={ref} 
                            id={inputId}
                            type={inputType}
                            className={inputClass}
                            readOnly={useVariant === "disabled"}
                            aria-labelledby={ariaLabelId}
                            autoComplete={autocomplete ? autocomplete : null}
                            onChange={(e) => {
                                if (onChange) {
                                    onChange(e);
                                }
                            }}
                            { ...otherProps }
                        />
                        <span id={ariaLabelId} className="sr-only">{ariaLabel}</span>
                    </>
                ) : (
                    <input
                        ref={ref} 
                        id={inputId}
                        type={inputType}
                        className={inputClass}
                        readOnly={useVariant === "disabled"}
                        autoComplete={autocomplete ? autocomplete : null}
                        onChange={(e) => {
                            if (onChange) {
                                onChange(e);
                            }
                        }}
                        { ...otherProps }
                    />
                )}
            </div>
            {(help || showHelp === "always") && (
                <>
                    {errorIcon ? (
                        <div className="flex items-center gap-2 mt-1.5">
                            <XMarkIcon className="text-red size-4 shrink-0 grow-0 mb-1"/>
                            <p 
                                className={`font-vg-book text-xs2 leading-[0.975rem] min-h-[0.975rem] grow ${variant === "error" ? "text-red" : (helpColor ? helpColor :"text-grey")}`}
                                dangerouslySetInnerHTML={{__html: help}}
                            />
                        </div>
                    ) : (
                        <p 
                            className={`font-vg-book text-xs2 leading-[0.975rem] mt-1.5 min-h-[0.975rem] ${variant === "error" ? "text-red" : (helpColor ? helpColor :"text-grey")}`}
                            dangerouslySetInnerHTML={{__html: help}}
                        />
                    )}
                </>
            )}
        </div>
    );
};

TextInput.displayName = 'TextInput';

export default TextInput;